import { useState, useEffect } from 'react';
import Select from 'react-select';
import { X, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONDITION_OPTIONS = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' }
];

// Format option label for goal select (moved outside component to avoid re-renders)
const formatGoalOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ 
            fontSize: '10px', 
            padding: '2px 6px', 
            borderRadius: '4px',
            backgroundColor: option.goalType === 'pageview' ? '#dbeafe' : 
                            option.goalType === 'custom_event' ? '#fef3c7' : '#f3e8ff',
            color: option.goalType === 'pageview' ? '#1d4ed8' : 
                   option.goalType === 'custom_event' ? '#b45309' : '#7c3aed'
        }}>
            {option.goalType === 'pageview' ? 'Page' : 
             option.goalType === 'custom_event' ? 'Event' : 'Scroll'}
        </span>
        <span>{option.label}</span>
    </div>
);

const GoalFilterModal = ({ isOpen, onClose, onApply, websiteId, token }) => {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRows, setFilterRows] = useState([
        { id: 1, condition: 'is', selectedGoals: [] }
    ]);

    // Fetch goals from API
    useEffect(() => {
        if (isOpen && websiteId && token) {
            fetchGoals();
        }
    }, [isOpen, websiteId, token]);

    const fetchGoals = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/goals/${websiteId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const goalOptions = response.data.map(g => ({
                value: g.id,
                label: g.name,
                goalType: g.goal_type,
                pagePath: g.page_path
            }));
            setGoals(goalOptions);
        } catch (error) {
            console.error('Failed to fetch goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConditionChange = (rowId, value) => {
        setFilterRows(rows =>
            rows.map(row =>
                row.id === rowId ? { ...row, condition: value } : row
            )
        );
    };

    const handleGoalChange = (rowId, selectedOptions) => {
        setFilterRows(rows =>
            rows.map(row =>
                row.id === rowId ? { ...row, selectedGoals: selectedOptions || [] } : row
            )
        );
    };

    const addFilterRow = () => {
        const newId = Math.max(...filterRows.map(r => r.id), 0) + 1;
        setFilterRows([...filterRows, { id: newId, condition: 'is', selectedGoals: [] }]);
    };

    const removeFilterRow = (rowId) => {
        if (filterRows.length > 1) {
            setFilterRows(rows => rows.filter(row => row.id !== rowId));
        }
    };

    const handleApply = () => {
        const filters = [];
        
        filterRows.forEach(row => {
            if (row.selectedGoals.length > 0) {
                // Format: condition,goal,goalId:goalName|goalId:goalName
                // Include goal name so filter banner can display it
                const goalValues = row.selectedGoals.map(g => `${g.value}:${g.label}`).join('|');
                filters.push(`${row.condition},goal,${goalValues}`);
            }
        });

        if (filters.length > 0) {
            onApply(filters);
        }
        onClose();
    };

    const handleReset = () => {
        setFilterRows([{ id: 1, condition: 'is', selectedGoals: [] }]);
    };

    if (!isOpen) return null;

    return (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={modalStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <h3 style={titleStyle}>Filter by Goal</h3>
                    <button onClick={onClose} style={closeBtnStyle}>
                        <X size={18} />
                    </button>
                </div>

                {/* Filter Rows */}
                <div style={{ marginBottom: '16px' }}>
                    {filterRows.map((row, index) => (
                        <div key={row.id} style={filterRowStyle}>
                            <div style={{ display: 'flex', gap: '10px', flex: 1, alignItems: 'center' }}>
                                {/* Condition Dropdown */}
                                <select
                                    value={row.condition}
                                    onChange={e => handleConditionChange(row.id, e.target.value)}
                                    style={selectStyle}
                                >
                                    {CONDITION_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>

                                {/* Goal Multi-Select */}
                                <div style={{ flex: 1 }}>
                                    <Select
                                        options={goals}
                                        value={row.selectedGoals}
                                        onChange={val => handleGoalChange(row.id, val)}
                                        isMulti
                                        isClearable
                                        isLoading={loading}
                                        placeholder="Select a Goal"
                                        styles={reactSelectStyles}
                                        formatOptionLabel={formatGoalOptionLabel}
                                    />
                                </div>

                                {/* Delete Button */}
                                {filterRows.length > 1 && (
                                    <button
                                        onClick={() => removeFilterRow(row.id)}
                                        style={deleteButtonStyle}
                                        title="Remove filter"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Add Another Link */}
                <button onClick={addFilterRow} style={addAnotherStyle}>
                    <Plus size={14} />
                    <span>Add another</span>
                </button>

                {/* Footer */}
                <div style={footerStyle}>
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        style={{ marginRight: '10px' }}
                    >
                        Reset
                    </Button>
                    <Button
                        onClick={handleApply}
                        disabled={filterRows.every(r => r.selectedGoals.length === 0)}
                        style={{ backgroundColor: '#6366f1', color: '#fff' }}
                    >
                        Apply filter
                    </Button>
                </div>
            </div>
        </div>
    );
};

/* Styles */
const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
};

const modalStyle = {
    width: '500px',
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
};

const titleStyle = {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f172a',
    letterSpacing: '-0.01em'
};

const filterRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px'
};

const footerStyle = {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end'
};

const closeBtnStyle = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    width: 28,
    height: 28,
    borderRadius: 9999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s, color 0.15s'
};

const selectStyle = {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    background: '#fff',
    color: '#0f172a',
    minWidth: '100px',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const deleteButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
};

const addAnotherStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    color: '#6366f1',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '4px 0',
    marginBottom: '8px'
};

const reactSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        fontSize: 14,
        borderRadius: 12,
        borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(99, 102, 241, 0.12)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)',
        '&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
        transition: 'box-shadow 0.2s, border-color 0.2s'
    }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, color: '#0f172a' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', padding: 6 }),
    menu: (base) => ({
        ...base,
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        color: '#0f172a',
        backgroundColor: state.isSelected ? '#eef2ff' : state.isFocused ? '#f8fafc' : 'transparent',
        cursor: 'pointer'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eef2ff',
        borderRadius: 9999,
        paddingLeft: 4,
        paddingRight: 2
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#4338ca',
        fontSize: 12,
        fontWeight: 500
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#4338ca',
        borderRadius: 9999,
        ':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
    })
};

export default GoalFilterModal;
